
import PaymentMode from '@/Models/Static/PaymentMode'

// MODULES
import standard from './standard'
import payPerItems from './payPerItems'
import divide from './divide'

const modules = {
  standard,
  payPerItems,
  divide,
}

const state = () => ({
  /**
   * This is the current payment mode.
   * @type Number
   */
  mode: PaymentMode.STANDARD,

  /**
   * Determine if the current payment is the first (to create an Invoice).
   * @type Boolean
   */
  first: true,

  /**
   * This is the amount the customer should pay.
   * @type Number
   */
  amountToPay: 0,

  /**
   * This is the amount the customer has already paid.
   * @type Number
   */
  paidAmount: 0,

  /**
   * This is the payment's tip amount.
   * @type Number
   */
  tipAmount: 0,
})

const getters = {
  standard: state => state.mode == PaymentMode.STANDARD,
  payingPerItems: state => state.mode == PaymentMode.SPLIT_ITEMS,
  dividing: state => state.mode == PaymentMode.SPLIT_PERSONS,

  willBeChange: state => payingAmount => Number(payingAmount).greaterThan(state.amountToPay + state.tipAmount),

  module: state => ['standard', 'payPerItems', 'divide'][state.mode],

  notAccountablePaymentMethods: (state, getters, rootState, rootGetters) =>
    rootGetters['fiscalCountry/config']('not_accountable_payment_methods')
      ?.map(o => parseInt(o)) ?? [],

  paymentMethodIsNotAccountable: (state, getters) => paymentMethod =>
    getters['notAccountablePaymentMethods'].includes(paymentMethod),

  mixedAccountablePayments: (state, getters, rootState, rootGetters) => paymentMethod => {
    const notAccountable = getters['paymentMethodIsNotAccountable'](paymentMethod)
    const invoice = rootState.orders.current.getInvoice(state.mode)
    if (! invoice) {
      return false
    }
    if (invoice.notAccountable && ! notAccountable) {
      return true
    }
    if (! invoice.notAccountable && notAccountable && ! state.first) {
      return true
    }
    return false
  },
}

const mutations = {
  mode (state, v) {
    state.mode = v
  },
  first (state, v = true) {
    state.first = v
  },
  amountToPay (state, v) {
    state.amountToPay = v
  },
  paidAmount (state, v) {
    state.paidAmount = v
  },
  pay (state, v) {
    state.amountToPay -= v
    state.paidAmount += v
    state.tipAmount = 0
  },
  tipAmount (state, v) {
    state.tipAmount = v
  },

  payingPerItems (state) {
    state.mode = PaymentMode.SPLIT_ITEMS
  },
}

const actions = {
  reset ({ commit, dispatch, rootState }) {
    commit('divide/customers', 0)
    dispatch('set', {
      mode: PaymentMode.STANDARD,
      amount: rootState.orders.current.amountToPay,
    })
  },
  payingPerItems ({ dispatch }, amount) {
    dispatch('set', {
      mode: PaymentMode.SPLIT_ITEMS,
      amount,
    })
  },
  divide ({ state, commit, dispatch, rootState }, divisor) {
    const amount = rootState.orders.current.amountToPay
    commit('divide/customers', divisor)
    commit('divide/amount', amount)
    dispatch('set', {
      mode: PaymentMode.SPLIT_PERSONS,
      amount: state.divide.amount,
    })
  },
  set ({ getters, commit, dispatch, rootState }, { mode, amount }) {
    commit('mode', mode)
    commit('first', mode == PaymentMode.SPLIT_ITEMS || ! Boolean(rootState.orders.current.liveInvoice))
    commit('amountToPay', amount)
    commit('paidAmount', mode == PaymentMode.STANDARD ? rootState.orders.current.alreadyPaid : 0)
    dispatch(getters['module'] + '/reset')
  },
  pay ({ state, getters, commit, dispatch }, amount) {
    commit('pay', amount)
    commit('first', false)
    if (Number(state.amountToPay).lessThanOrEqualTo(0)) {
      dispatch(getters['module'] + '/paid')
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
  modules
}
